import { onLoginAPI, onGoogleLoginAPI, onLogoutAPI, onMFASetupAPI, onMFAVerifyAPI, onMFASetupVerifyAPI } from 'api/AuthAPI';
import { all, call, Effect, put, takeLatest } from 'redux-saga/effects';
import {
    loginRequestFailure,
    loginRequestSuccess,
    googleLoginRequestFailure,
    googleLoginRequestSuccess,
    mfaSetupRequestSuccess,
    mfaSetupRequestFailure,
    mfaVerifyRequestSuccess,
    mfaVerifyRequestFailure,
    logoutRequestFailure,
    setToastAction,
    userRequest,
    mfaSetupVerifyRequestSuccess,
    mfaSetupVerifyRequestFailure,
    getMFADetailsAction,
} from 'redux/actions';
import { LoginResponse } from 'redux/types/authActionModel';
import * as actionTypes from '../actionTypes';
import { logoutService } from 'shared/service/CommonService';
import { ToastVariant } from 'shared/utils/Constants';

/*
  Worker Saga: Fired on LOGIN_REQUEST action
*/
function* fetchLoginSaga(action: Effect) {
    try {
        const response: LoginResponse = yield call(onLoginAPI, action.payload);
        // Check if MFA is required
        if (response.mfa_required && !response.mfa_setup_required) {
            // MFA verification needed - don't set tokens yet
            yield put(loginRequestSuccess(response));
        } else if (response.mfa_setup_required) {
            // MFA setup needed - redirect to setup
            yield put(loginRequestSuccess(response));
        } else if (response.access_token && response.refresh_token) {
            // Normal login success
            yield put(loginRequestSuccess(response));
            yield put(userRequest());
        } else {
            throw new Error('Invalid login response');
        }
    } catch (e: any) {        
        yield put(
            loginRequestFailure({
                error: e?.response?.data,
            }),
        );
    }
}

function* fetchGoogleLoginSaga(action: Effect) {
    try {
        const response: LoginResponse = yield call(onGoogleLoginAPI, action.payload);
        // Check if MFA is required
        if (response.mfa_required && !response.mfa_setup_required) {
            // MFA verification needed
            yield put(googleLoginRequestSuccess(response));
        } else if (response.mfa_setup_required) {
            // MFA setup needed
            yield put(googleLoginRequestSuccess(response));
        } else if (response.access_token && response.refresh_token) {
            // Normal login success
            yield put(googleLoginRequestSuccess(response));
            yield put(userRequest());
        } else {
            throw new Error('Invalid login response');
        }
    } catch (e: any) {
        yield put(
            googleLoginRequestFailure({
                error: e?.response?.data,
            }),
        );
    }
}

function* fetchLogoutSaga() {
    try {
        const response: string = yield call(onLogoutAPI);
        logoutService();
        yield put(setToastAction(ToastVariant.SUCCESS, response));
    } catch (e: any) {
        yield put(logoutRequestFailure({ error: e?.response?.data }));
    }
}

/*
  Starts worker saga on latest dispatched `LOGIN_REQUEST` action.
*/
function* fetchMFASetupSaga() {
    try {
        const response: { qrCode: string; secret: string } = yield call(onMFASetupAPI);
        yield put(mfaSetupRequestSuccess(response));
    } catch (e: any) {
        yield put(
            mfaSetupRequestFailure({
                error: e?.response?.data,
            }),
        );
    }
}

function* fetchMFAVerifySaga(action: Effect) {
    try {
        const response: LoginResponse = yield call(onMFAVerifyAPI, action.payload);
        yield put(mfaVerifyRequestSuccess(response));
        yield put(userRequest());
    } catch (e: any) {
        yield put(
            mfaVerifyRequestFailure({
                error: e?.response?.data?.error,
            }),
        );
    }
}

function* fetchMFASetupVerifySaga(action: Effect) {
    try {
        const response: LoginResponse = yield call(
            onMFASetupVerifyAPI,
            action.payload
        );

        yield put(mfaSetupVerifyRequestSuccess(response));
        yield put(userRequest());
        yield put(getMFADetailsAction());
    } catch (e: any) {
        yield put(
            mfaSetupVerifyRequestFailure({
                error: e?.response?.data,
            }),
        );
    }
}


function* authSaga() {
    yield all([takeLatest(actionTypes.LOGIN_REQUEST, fetchLoginSaga)]);
    yield all([takeLatest(actionTypes.GOOGLE_LOGIN_REQUEST, fetchGoogleLoginSaga)]);
    yield all([takeLatest(actionTypes.MFA_SETUP_REQUEST, fetchMFASetupSaga)]);
    yield all([takeLatest(actionTypes.MFA_VERIFY_REQUEST, fetchMFAVerifySaga)]);
    yield all([takeLatest(actionTypes.MFA_SETUP_VERIFY_REQUEST, fetchMFASetupVerifySaga)]);
    yield all([takeLatest(actionTypes.LOGOUT_REQUEST, fetchLogoutSaga)]);
}

export default authSaga;
